import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  BrowserMultiFormatReader,
  DecodeHintType,
  HTMLCanvasElementLuminanceSource,
  BinaryBitmap,
  HybridBinarizer,
} from '@zxing/library';
import './BarcodeScanner.css';

const hints = new Map();
hints.set(DecodeHintType.TRY_HARDER, true);

interface BarcodeScannerProps {
  visible: boolean;
  onScan: (result: string) => void;
  onClose: () => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  visible,
  onScan,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const frameCountRef = useRef(0);
  const lastScanTimeRef = useRef(0);
  const foundRef = useRef(false);

  const addLog = useCallback((msg: string) => {
    setDebugLog(prev => [...prev.slice(-4), msg]);
  }, []);

  useEffect(() => {
    if (!visible) return;
    foundRef.current = false;
    frameCountRef.current = 0;
    setDebugLog(['Initializing...']);
    readerRef.current = new BrowserMultiFormatReader(hints);
    startCamera();

    return () => {
      cancelAnimationFrame(rafRef.current);
      stopStream();
      if (readerRef.current) {
        readerRef.current.reset();
        readerRef.current = null;
      }
    };
  }, [visible]);

  const startCamera = async () => {
    try {
      addLog('Requesting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();

      const dims = `${video.videoWidth}x${video.videoHeight}`;
      addLog(`Camera: ${dims}`);
      scanLoop();
    } catch (err) {
      const e = err as Error;
      addLog(`Camera error: ${e.name}`);
      if (e.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access.');
      } else if (e.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError(`Camera error: ${e.name} - ${e.message}`);
      }
    }
  };

  const scanLoop = () => {
    if (foundRef.current) return;

    const now = performance.now();
    // Scan every ~150ms (~7 fps) to avoid CPU overload on mobile
    if (now - lastScanTimeRef.current >= 150) {
      lastScanTimeRef.current = now;
      tryDecode();
    }

    rafRef.current = requestAnimationFrame(scanLoop);
  };

  const tryDecode = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const reader = readerRef.current;
    if (!video || !canvas || !reader || video.readyState < 2) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;

    // Crop to center 80% width, 30% height (the guide box area)
    const cropW = Math.round(vw * 0.8);
    const cropH = Math.round(vh * 0.3);
    const cropX = Math.round((vw - cropW) / 2);
    const cropY = Math.round((vh - cropH) / 2);

    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    frameCountRef.current++;
    if (frameCountRef.current % 15 === 0) {
      addLog(`Frames: ${frameCountRef.current} (crop: ${cropW}x${cropH})`);
    }

    try {
      const luminance = new HTMLCanvasElementLuminanceSource(canvas);
      const bitmap = new BinaryBitmap(new HybridBinarizer(luminance));
      const result = reader.decodeBitmap(bitmap);

      if (result) {
        addLog(`FOUND: ${result.getText()} (${result.getBarcodeFormat()})`);
        foundRef.current = true;
        cancelAnimationFrame(rafRef.current);
        stopStream();
        onScan(result.getText());
        onClose();
      }
    } catch {
      // No barcode found in this frame — continue scanning
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleClose = () => {
    cancelAnimationFrame(rafRef.current);
    stopStream();
    if (readerRef.current) {
      readerRef.current.reset();
      readerRef.current = null;
    }
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="scanner-fullscreen">
      <div className="scanner-header">
        <h3 className="scanner-title">Scan your VIN Barcode</h3>
      </div>

      <div className="scanner-video-area">
        {error ? (
          <div className="scanner-error">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="scanner-video"
              playsInline
              autoPlay
              muted
            />
            <div className="scanner-guide">
              <div className="scanner-guide-box" />
            </div>
          </>
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="scanner-debug">
        {debugLog.map((msg, i) => (
          <div key={i}>{msg}</div>
        ))}
      </div>

      <div className="scanner-footer">
        <button className="scanner-cancel-btn" onClick={handleClose}>
          Cancel Scan
        </button>
      </div>
    </div>
  );
};

export default BarcodeScanner;